import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./capability-04.css";
import scalableWorkVideo from "../../assets/capability/Smart Client Tracker.mp4";
import { useLanguage } from "../../i18n/LanguageContext";

gsap.registerPlugin(ScrollTrigger);

export default function Capability04() {
  const rootRef = useRef(null);
  const { tr } = useLanguage();

  useEffect(() => {
    const ctx = gsap.context(() => {
      const root = rootRef.current;
      if (!root) return;

      const isMobile = window.innerWidth <= 768;

      // Refresh ScrollTrigger saat resize
      const handleResize = () => {
        ScrollTrigger.refresh();
      };
      window.addEventListener("resize", handleResize);

      // === FADE SCROLL HINT (INTRO) ===
      // Adapted from provided logic
      gsap.to(".cap04-scroll", {
        autoAlpha: 0,
        duration: 0.2,
        scrollTrigger: {
          trigger: document.body,
          start: "top top",
          end: "top top-=1",
          toggleActions: "play none reverse none",
        },
      });

      // === LETTER SCROLL ANIMATION ===
      // Adapted from provided logic
      const hcList = root.querySelector(".cap04-list");
      if (hcList && !isMobile) {
        gsap.to(".cap04-letter", {
          yPercent: 100,
          ease: "power1.inOut",
          scrollTrigger: {
            trigger: hcList,
            start: "33.33% bottom",
            end: "100% 80%",
            scrub: 1,
          },
          stagger: {
            each: 0.05,
            from: "random",
          },
        });
      }

      // === SLIDE PPT STYLE (like capability.jsx) ===
      // Set initial state
      gsap.set(root, { autoAlpha: 1, y: 0 });

      if (hcList) {
        gsap.set(hcList, { opacity: 1, y: 0 });
      }

      // Mobile: Skip pinning and complex fade animations
      if (isMobile) {
        // Mobile: no title animation

        const slideHeight = window.innerHeight;

        // Pin section for full viewport height (like PowerPoint)
        ScrollTrigger.create({
          trigger: root,
          start: "top top",
          end: `+=${slideHeight}`,
          pin: root,
          pinSpacing: true,
          scrub: false,
        });
      } else {
        // Desktop: Full pinning and fade animations
      const slideHeight = window.innerHeight;

      // Pin section for full viewport height (like PowerPoint)
      ScrollTrigger.create({
        trigger: root,
        start: "top top",
        end: `+=${slideHeight}`,
        pin: root,
        pinSpacing: true,
        scrub: false,
      });

      // Fade in animation when section enters viewport (support scroll up)
        // Only fade content, not background
        if (hcList) {
      gsap.fromTo(
            hcList,
            { opacity: 0, y: 30 },
        {
              opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: root,
            start: "top 80%",
            end: "top 20%",
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        }

      // Fade out animation when section exits (support scroll up)
        // Only fade content, keep background visible to prevent black screen
        if (hcList) {
      gsap.fromTo(
            hcList,
            { opacity: 1, y: 0 },
        {
              opacity: 0,
          y: -30,
          duration: 1,
          ease: "power2.in",
          scrollTrigger: {
            trigger: root,
            start: "bottom top",
            end: `bottom+=${slideHeight * 0.5} top`,
            scrub: true,
            invalidateOnRefresh: true,
          },
        }
      );
        }
      }

      // Refresh setelah setup
      ScrollTrigger.refresh();

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div style={{ backgroundColor: "#D8D8D8" }}>
      <section id="solutions-04" className="cap04-section" ref={rootRef}>
        

        <div className="cap04-header">
          <ul className="cap04-list cap04-list-horizontal">
          <li>
              {tr("Smart", "Smart").split("").map((char, i) => (
                <span key={i} className="cap04-letter">
                <span>{char}</span>
                <span>{char}</span>
              </span>
            ))}
          </li>

          <li>
              {tr("Client", "Client").split("").map((char, i) => (
                  <span key={i} className="cap04-letter">
                  <span>{char}</span>
                  <span>{char}</span>
                </span>
              ))}
          </li>

          <li>
              {tr("Tracker", "Tracker").split("").map((char, i) => (
                <span key={i} className="cap04-letter">
                <span>{char}</span>
                <span>{char}</span>
              </span>
            ))}
          </li>
        </ul>
        </div>
        
        <p className="cap04-paragraph">
          {tr(
            "Auto-log client convos, requests, and deadlines into one place - so you never have to dig through chats or notes",
            "Otomatis mencatat percakapan klien, request, dan deadline ke satu tempat — jadi kamu tidak perlu menggali chat atau catatan"
          )}
        </p>
        <p className="cap04-paragraph cap04-note">
          {tr(
            "Ideal for professional service providers who manage multiple projects without a full-blown CRM",
            "Ideal untuk professional service provider yang mengelola banyak proyek tanpa CRM penuh"
          )}
        </p>
        <p className="cap04-num cap04-num-bottom">(04)</p>
        <video
          className="cap04-video"
          src={scalableWorkVideo}
          autoPlay
          loop
          muted
          playsInline
        />
      </section>
    </div>

  );
}
